import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart3, BookMarked, BookOpen, Brain, Calculator, Cpu, Database, FileText, FolderClock, Gavel, Globe, GraduationCap, Handshake, Landmark,
  Newspaper, Pin, Radar, Scale, ShieldCheck, Undo2, Workflow, type LucideIcon,
} from "lucide-react";
import { useApi } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { bi, type Bi, type MemoryItem, type Mission, type MissionState, type SourceKey } from "../mission/engine";
import { day } from "../mission/text";
import type { Widget } from "../mission/widgets";
import { useT } from "./kit";

// ---------------------------------------------------------------- memory

export const MEMORY_ICON: Record<MemoryItem["kind"], LucideIcon> = { case: FolderClock, transaction: Handshake, commitment: ShieldCheck, lesson: GraduationCap, practice: BookMarked, fact: Pin, decision: Gavel };
export const MEMORY_COLOR: Record<MemoryItem["kind"], string> = {
  case: "var(--agx-memory)", transaction: "var(--agx-gold)", commitment: "var(--agx-amber)", lesson: "var(--agx-green)", practice: "var(--agx-violet)", fact: "var(--agx-cyan)", decision: "var(--agx-gold)",
};
const KIND_ORDER: Array<MemoryItem["kind"]> = ["practice", "decision", "fact", "case", "transaction", "commitment", "lesson"];

/** The memory agent's result: what the Department already knew, by kind (a compact stack on the card, a timeline per kind at full size). */
export function MemoryWidget({ w, size }: { w: Extract<Widget, { kind: "memory" }>; size: "card" | "focus" }) {
  const t = useT();
  const { t: tr } = useTranslation();
  const kinds = KIND_ORDER.filter((k) => w.entries.some((e) => e.kind === k));
  if (size === "card") {
    return (
      <div className="flex h-full flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-1">
          {kinds.length ? kinds.map((k) => {
            const Icon = MEMORY_ICON[k];
            return <span key={k} className="agx-chip" style={{ ["--c" as string]: MEMORY_COLOR[k] }} title={tr(`backoffice:agentic.memory.kinds.${k}`)}><Icon className="size-2.5" />{w.entries.filter((e) => e.kind === k).length}</span>;
          }) : <span className="agx-shimmer text-[11px]">{tr("backoffice:agentic.memory.kicker")}…</span>}
        </div>
        <div className="min-h-0 space-y-[3px] overflow-hidden">
          {w.entries.slice(-3).reverse().map((e, i) => {
            const Icon = MEMORY_ICON[e.kind];
            return (
              <motion.p key={`${e.title.en}-${i}`} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} className="flex min-w-0 items-center gap-1.5 text-[11px]">
                <Icon className="size-3 shrink-0" style={{ color: MEMORY_COLOR[e.kind] }} />
                <span className="truncate text-[color:var(--agx-text)]/90">{t(e.title)}</span>
              </motion.p>
            );
          })}
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-5">
      {kinds.map((k) => {
        const Icon = MEMORY_ICON[k];
        const entries = w.entries.filter((e) => e.kind === k);
        return (
          <section key={k}>
            <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color: MEMORY_COLOR[k] }}>
              <Icon className="size-3.5" />{tr(`backoffice:agentic.memory.kinds.${k}`)} · {entries.length}
            </p>
            <ol className="space-y-2.5 border-s border-[color:var(--agx-line-2)] ps-4">
              {entries.map((e, i) => (
                <motion.li key={`${e.title.en}-${i}`} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} className="relative">
                  <span className="absolute -start-[21.5px] top-[7px] size-2.5 rounded-full" style={{ background: MEMORY_COLOR[k] }} />
                  <p className="text-[13px] font-medium leading-[1.45]">{t(e.title)}</p>
                  {e.detail ? <p className="text-[12px] leading-[1.45] agx-dim">{t(e.detail)}</p> : null}
                  {e.source || e.when ? <p className="mt-0.5 text-[11px] agx-faint">{e.source ? t(e.source) : ""}{e.source && e.when ? " · " : ""}{e.when ? t(day(e.when)) : ""}</p> : null}
                </motion.li>
              ))}
            </ol>
          </section>
        );
      })}
      {!kinds.length ? <p className="agx-shimmer text-[13px]">{tr("backoffice:agentic.memory.emptyRecalled")}</p> : null}
    </div>
  );
}

/**
 * One memory in a conversation (an agent's box, the lead's stream or the Memory tab): recalled from the Department's memory, or
 * remembered for later files with its state in the store and a control to forget it.
 */
export function MemoryNote({ item, mission, state, size, onAgent }: { item: MemoryItem; mission: Mission; state?: MissionState; size: "card" | "full" | "lead"; onAgent?: (id: string) => void }) {
  const t = useT();
  const { t: tr } = useTranslation();
  const Icon = MEMORY_ICON[item.kind];
  const recall = item.mode === "recall";
  const agent = state && item.agentId !== "lead" ? state.agents[item.agentId] : undefined;
  const who = state ? (agent ? t(agent.name) : tr("backoffice:agentic.lead.name")) : null;
  const stored = !recall && !!item.serverId && !item.forgotten;
  const compact = size === "card";
  return (
    <div className={`agx-mem ${compact ? "" : size === "full" ? "px-3 py-2 text-[13px] leading-[1.55]" : "p-2.5 text-[12.5px] leading-[1.5]"}`} data-mode={item.mode} data-forgotten={item.forgotten ? "true" : undefined}>
      <span className="agx-mem-kicker">
        <Brain className="size-3" />
        {recall ? tr("backoffice:agentic.memory.recalledFrom") : tr("backoffice:agentic.memory.rememberedFor")}
        {!compact ? <span className="font-medium normal-case tracking-normal opacity-75">· {tr(`backoffice:agentic.memory.kinds.${item.kind}`)}</span> : null}
        {who ? (
          <button type="button" onClick={() => onAgent?.(item.agentId)} className="ms-auto truncate font-medium normal-case tracking-normal agx-dim hover:text-[color:var(--agx-text)]">{who}</button>
        ) : null}
      </span>
      <span className="mt-0.5 flex gap-1.5">
        <Icon className="mt-[3px] size-3 shrink-0" style={{ color: MEMORY_COLOR[item.kind] }} />
        <span className={`min-w-0 ${compact ? "line-clamp-2" : "font-medium"}`}>{t(item.title)}</span>
      </span>
      {item.detail && !compact ? <span className="mt-0.5 block text-[0.92em] agx-dim">{t(item.detail)}</span> : null}
      {recall ? (item.source && !compact ? <span className="mt-0.5 block text-[11px] agx-faint">↳ {t(item.source)}</span> : null) : (
        <span className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
          {item.forgotten ? <span className="agx-faint">{tr("backoffice:agentic.memory.forgotten")}</span>
            : stored ? <span style={{ color: "var(--agx-green)" }}>✓ {tr("backoffice:agentic.memory.kept")}</span>
            : item.source ? <span style={{ color: "var(--agx-rose)" }}>{t(item.source)}</span>
            : <span className="agx-shimmer">…</span>}
          {stored && !compact ? (
            <button type="button" onClick={(e) => { e.stopPropagation(); void mission.forgetMemory(item.id); }} className="agx-btn ms-auto h-6 px-2 text-[11px]" data-tone="ghost">
              <Undo2 className="size-3" />{tr("backoffice:agentic.memory.forget")}
            </button>
          ) : null}
        </span>
      )}
    </div>
  );
}

/** The lead's Memory tab: what this review taught the Department (with a way to take it back) and everything it recalled, by kind. */
export function MemoryPanel({ state, mission }: { state: MissionState; mission: Mission }) {
  const { t: tr } = useTranslation();
  const learned = state.memory.filter((m) => m.mode === "remember").slice().reverse();
  const recalled = state.memory.filter((m) => m.mode === "recall");
  const kinds = KIND_ORDER.filter((k) => recalled.some((m) => m.kind === k));
  const focus = (id: string) => { if (id !== "lead") mission.setFocus(id); };
  return (
    <div className="@container space-y-5 p-4">
      <div className="agx-mem-hero">
        <span className="agx-orb size-9 shrink-0" data-squad="memory" data-live={state.status === "running"}><Brain className="size-4" /></span>
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--agx-memory)]">{tr("backoffice:agentic.memory.kicker")}</p>
          <p className="mt-0.5 text-[12px] leading-[1.5] agx-dim">{tr("backoffice:agentic.memory.intro")}</p>
          <p className="mt-1.5 flex flex-wrap gap-1.5">
            <span className="agx-chip" style={{ ["--c" as string]: "var(--agx-memory)" }}>{tr("backoffice:agentic.memory.recalledCount", { count: recalled.length })}</span>
            <span className="agx-chip" style={{ ["--c" as string]: "var(--agx-gold)" }}>{tr("backoffice:agentic.memory.learnedCount", { count: learned.filter((m) => !m.forgotten).length })}</span>
          </p>
        </div>
      </div>

      <section>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--agx-gold)]">{tr("backoffice:agentic.memory.learnedHere")}</p>
        <div className="grid grid-cols-1 gap-2 @xl:grid-cols-2">
          {learned.map((m) => <MemoryNote key={m.id} item={m} mission={mission} state={state} size="lead" onAgent={focus} />)}
          {!learned.length ? <p className="text-[12px] leading-[1.5] agx-faint">{tr("backoffice:agentic.memory.emptyLearned")}</p> : null}
        </div>
      </section>

      <section>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--agx-memory)]">{tr("backoffice:agentic.memory.recalledFrom")}</p>
        {kinds.map((k) => (
          <div key={k} className="mb-2 grid grid-cols-1 gap-2 @xl:grid-cols-2">
            {recalled.filter((m) => m.kind === k).map((m) => <MemoryNote key={m.id} item={m} mission={mission} state={state} size="lead" onAgent={focus} />)}
          </div>
        ))}
        {!recalled.length ? <p className="text-[12px] leading-[1.5] agx-faint">{tr("backoffice:agentic.memory.emptyRecalled")}</p> : null}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------- sources

interface SourceMeta { icon: LucideIcon; color: string; name: Bi; short: Bi; detail: Bi; integrations?: string[] }

/** The systems behind the agents' tools, as the officer sees them: the Ministry's own, government registers, licensed feeds, the open web. */
export const SOURCE_META: Record<SourceKey, SourceMeta> = {
  documents: { icon: FileText, color: "var(--agx-cyan)", name: bi("Case file", "الملف"), short: bi("Case file", "الملف"), detail: bi("Filed documents and their extracted text", "المستندات المودعة ونصوصها المستخرجة") },
  rules: { icon: Scale, color: "var(--agx-violet)", name: bi("Competition rules engine", "محرك قواعد المنافسة"), short: bi("Rules", "القواعد"), detail: bi("Thresholds, Article 10, statutory clock, publication", "العتبات، المادة 10، المهلة النظامية، النشر") },
  registry: { icon: Landmark, color: "var(--agx-gold)", name: bi("National Economic Register", "السجل الاقتصادي الوطني"), short: bi("Register", "السجل"), detail: bi("Licences, legal form, activities, shareholders", "الرخص والشكل القانوني والأنشطة والشركاء"), integrations: ["Registry"] },
  fcsc: { icon: BarChart3, color: "var(--agx-green)", name: bi("FCSC statistics", "إحصاءات المركز الاتحادي للتنافسية والإحصاء"), short: bi("FCSC", "الإحصاء"), detail: bi("Market shares checked against official statistics", "مطابقة الحصص مع الإحصاءات الرسمية"), integrations: ["Fcsc"] },
  databank: { icon: Database, color: "var(--agx-memory)", name: bi("National Competition Data Bank", "بنك بيانات المنافسة الوطني"), short: bi("Data Bank", "بنك البيانات"), detail: bi("Market history, ownership graph, recorded deals", "تاريخ السوق وشبكة الملكية والصفقات المسجلة"), integrations: ["CompanySnapshot", "Securities", "Fca", "Ubo"] },
  economics: { icon: Calculator, color: "var(--agx-gold)", name: bi("Economics engine", "محرك التحليل الاقتصادي"), short: bi("Economics", "الاقتصاد"), detail: bi("HHI, GUPPI, CMCR, critical loss, merger simulation", "مؤشر هيرفندال وGUPPI وCMCR والخسارة الحرجة ومحاكاة الاندماج") },
  dealwatch: { icon: Radar, color: "var(--agx-orange)", name: bi("Deal Watch · Mergermarket", "مراقبة الصفقات · ميرجرماركت"), short: bi("Deal Watch", "مراقبة الصفقات"), detail: bi("Licensed M&A intelligence", "معلومات الاندماج والاستحواذ المرخصة"), integrations: ["Mergermarket"] },
  news: { icon: Newspaper, color: "var(--agx-pink)", name: bi("Market news monitor", "رصد أخبار السوق"), short: bi("News", "الأخبار"), detail: bi("UAE press and market feeds", "الصحافة الإماراتية وتغذيات السوق"), integrations: ["MarketFeed", "UaePress"] },
  corpus: { icon: BookOpen, color: "var(--agx-violet)", name: bi("Legal corpus", "المدونة القانونية"), short: bi("Legal corpus", "المدونة"), detail: bi("The law, regulations, guidelines and precedent", "القانون واللوائح والإرشادات والسوابق") },
  web: { icon: Globe, color: "var(--agx-green)", name: bi("Open web", "الويب المفتوح"), short: bi("Open web", "الويب"), detail: bi("Live web search, every page cited", "بحث مباشر في الويب مع ذكر كل صفحة"), integrations: ["LlmGateway"] },
  memory: { icon: Brain, color: "var(--agx-memory)", name: bi("Institutional memory", "الذاكرة المؤسسية"), short: bi("Memory", "الذاكرة"), detail: bi("Earlier files, deals, commitments, lessons, reviewers' practice", "الملفات السابقة والصفقات والتعهدات والدروس وممارسة المراجعين") },
  workflow: { icon: Workflow, color: "var(--agx-gold-2)", name: bi("Case workflow", "إجراء الملف"), short: bi("Workflow", "سير العمل"), detail: bi("Stages, transitions, declarations, overrides", "المراحل والانتقالات والإقرارات والتجاوزات") },
  runtime: { icon: Cpu, color: "var(--agx-gold-2)", name: bi("AI runtime", "وقت التشغيل الذكي"), short: bi("AI runtime", "التشغيل الذكي"), detail: bi("The platform's own agents drafting on the file", "وكلاء المنصة يصوغون على الملف"), integrations: ["LlmGateway"] },
};
const SOURCE_ORDER: SourceKey[] = ["documents", "memory", "rules", "registry", "fcsc", "databank", "economics", "dealwatch", "news", "corpus", "web", "workflow", "runtime"];

/** Re-renders every `ms` so "live" glows fade when nothing else changes. */
function useNow(ms: number): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(id);
  }, [ms]);
  return now;
}
const LIVE_MS = 2600;

/** The strip over the canvas: every source the agents can reach, lighting up as they reach it, and the review's memory at a glance. */
export function SourcesStrip({ state, onOpen }: { state: MissionState; onOpen: (tab: "memory" | "sources") => void }) {
  const t = useT();
  const { t: tr } = useTranslation();
  const now = useNow(500);
  const recalled = state.memory.filter((m) => m.mode === "recall").length;
  const learned = state.memory.filter((m) => m.mode === "remember" && !m.forgotten).length;
  const memoryLive = state.memory.some((m) => now - m.at < LIVE_MS);
  return (
    <div className="flex min-w-0 items-center gap-2 px-1">
      <button type="button" onClick={() => onOpen("sources")} className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.14em] agx-faint hover:text-[color:var(--agx-text)]">{tr("backoffice:agentic.sources.strip")}</button>
      <div className="agx-noscroll flex min-w-0 flex-1 items-center gap-1 overflow-x-auto">
        {SOURCE_ORDER.map((key) => {
          const meta = SOURCE_META[key];
          const use = state.sources[key];
          const live = !!use?.lastAt && now - use.lastAt < LIVE_MS;
          const Icon = meta.icon;
          return (
            <motion.button key={key} type="button" layout onClick={() => onOpen("sources")} className="agx-src" data-reached={!!use} data-live={live} data-error={!!use && use.errors > 0 && use.errors === use.calls}
              style={{ ["--c" as string]: meta.color }} title={`${t(meta.name)} · ${t(meta.detail)}${use ? ` · ${tr("backoffice:agentic.sources.calls", { count: use.calls })}` : ""}`}>
              <Icon className="size-3 shrink-0" />
              {live || (use && key === "web") ? <span className="whitespace-nowrap">{t(meta.short)}</span> : null}
              {use ? <span className="agx-mono">{use.calls}</span> : null}
            </motion.button>
          );
        })}
      </div>
      <button type="button" onClick={() => onOpen("memory")} className="agx-src shrink-0" data-reached={recalled + learned > 0} data-live={memoryLive} style={{ ["--c" as string]: "var(--agx-memory)" }}>
        <Brain className="size-3 shrink-0" />
        <span className="whitespace-nowrap">{tr("backoffice:agentic.memory.recalledCount", { count: recalled })}</span>
        {learned ? <span className="whitespace-nowrap" style={{ color: "var(--agx-gold)" }}>· {tr("backoffice:agentic.memory.learnedCount", { count: learned })}</span> : null}
      </button>
    </div>
  );
}

interface Integration { key: string; name: string; nameAr?: string | null; owner?: string | null; health: "Healthy" | "Degraded" | "Down" | "Unknown"; calls24h: number; circuitOpen?: boolean }
const HEALTH_COLOR: Record<Integration["health"], string> = { Healthy: "var(--agx-green)", Degraded: "var(--agx-amber)", Down: "var(--agx-rose)", Unknown: "var(--agx-faint)" };

/** The lead's Sources tab: what each source was asked in this review, and every system connected to the platform with its health. */
export function SourcesPanel({ state, mission }: { state: MissionState; mission: Mission }) {
  const t = useT();
  const { t: tr } = useTranslation();
  const api = useApi();
  const now = useNow(1000);
  const integrations = useQuery({ queryKey: ["agentic", "integrations"], queryFn: () => api.get<Integration[]>("/api/integrations"), staleTime: 60_000, retry: false });
  const reached = SOURCE_ORDER.filter((k) => state.sources[k]).sort((a, b) => (state.sources[b]!.lastAt ?? 0) - (state.sources[a]!.lastAt ?? 0));
  const used = new Set(reached.flatMap((k) => SOURCE_META[k].integrations ?? []));
  const nameOf = (id: string) => (id === "lead" ? tr("backoffice:agentic.lead.name") : state.agents[id] ? t(state.agents[id]!.name) : id);
  return (
    <div className="@container space-y-5 p-4">
      <div className="agx-mem-hero">
        <span className="agx-orb size-9 shrink-0" data-squad="intel" data-live={state.status === "running"}><Globe className="size-4" /></span>
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--agx-green)]">{tr("backoffice:agentic.sources.kicker")}</p>
          <p className="mt-0.5 text-[12px] leading-[1.5] agx-dim">{tr("backoffice:agentic.sources.intro")}</p>
        </div>
      </div>

      <section>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] agx-faint">{tr("backoffice:agentic.sources.reached")} · {reached.length}</p>
        <div className="grid grid-cols-1 gap-2 @xl:grid-cols-2">
          {reached.map((key) => {
            const meta = SOURCE_META[key];
            const use = state.sources[key]!;
            const live = !!use.lastAt && now - use.lastAt < LIVE_MS;
            const Icon = meta.icon;
            return (
              <motion.div key={key} layout className="agx-src-row" data-live={live} style={{ ["--c" as string]: meta.color }}>
                <span className="grid size-8 shrink-0 place-items-center rounded-[5px]" style={{ background: `color-mix(in oklab, ${meta.color} 16%, transparent)`, color: meta.color }}><Icon className="size-4" /></span>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 text-[12.5px] font-semibold">
                    <span className="truncate">{t(meta.name)}</span>
                    {live ? <span className="agx-chip" style={{ ["--c" as string]: meta.color }}><span className="agx-live-dot" />{tr("backoffice:agentic.sources.live")}</span> : null}
                  </p>
                  <p className="truncate text-[11px] agx-faint">{t(meta.detail)}</p>
                  {use.last ? <p className="mt-0.5 truncate text-[11.5px] agx-dim" dir="auto">↳ {t(use.last)}</p> : null}
                  <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] agx-faint">
                    <span className="agx-mono" style={{ color: meta.color }}>{tr("backoffice:agentic.sources.calls", { count: use.calls })}</span>
                    {use.errors ? <span style={{ color: "var(--agx-rose)" }}>{tr("backoffice:agentic.sources.failed", { count: use.errors })}</span> : null}
                    <span className="min-w-0 truncate">
                      {use.agents.slice(0, 3).map((id, i) => (
                        <button key={id} type="button" onClick={() => { if (id !== "lead") mission.setFocus(id); }} className="hover:text-[color:var(--agx-text)]">{i ? ", " : ""}{nameOf(id)}</button>
                      ))}
                      {use.agents.length > 3 ? ` +${use.agents.length - 3}` : ""}
                    </span>
                  </p>
                </div>
              </motion.div>
            );
          })}
          {!reached.length ? <p className="text-[12px] agx-faint">{tr("backoffice:agentic.sources.none")}</p> : null}
        </div>
      </section>

      <section>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] agx-faint">{tr("backoffice:agentic.sources.connected")}{integrations.data ? ` · ${integrations.data.length}` : ""}</p>
        {integrations.isError ? <p className="text-[12px] agx-faint">{tr("backoffice:agentic.sources.unavailable")}</p> : null}
        <div className="grid grid-cols-1 gap-1.5 @lg:grid-cols-2">
          {(integrations.data ?? []).map((x) => (
            <div key={x.key} className="flex min-w-0 items-center gap-2 rounded-[5px] border border-[color:var(--agx-line)] agx-wash px-2.5 py-1.5" data-used={used.has(x.key)} style={used.has(x.key) ? { borderColor: "color-mix(in oklab, var(--agx-green) 40%, transparent)" } : undefined}>
              <span className="size-2 shrink-0 rounded-full" style={{ background: x.circuitOpen ? "var(--agx-rose)" : HEALTH_COLOR[x.health] }} title={tr(`backoffice:agentic.sources.health.${x.health}`)} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[12px] font-medium">{t(bi(x.name, x.nameAr ?? x.name))}</span>
                <span className="block truncate text-[11px] agx-faint">{x.owner ?? ""}{x.owner ? " · " : ""}{tr(`backoffice:agentic.sources.health.${x.health}`)}</span>
              </span>
              <span className="agx-mono shrink-0 text-[11px] agx-faint" title={tr("backoffice:agentic.sources.calls24h", { count: x.calls24h })}>{x.calls24h}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
